import type {
  IntegratorActiveProduct,
  IntegratorInitGameOptions,
  IntegratorProductTarget,
} from '@nsftx/seven-client-retail-vue-integrator'
import type { IntegratorParentPOSConfig } from '@nsftx/seven-client-retail-vue-integrator'
import {
  createPosGameConfiguration,
  type CmsPosGameConfiguration,
} from '@nsftx/seven-client-retail-vue-sgp'
import type { GameLinkView } from '@nsftx/seven-client-retail-vue-sgp'
import { useShortcutsStore } from '@nsftx/seven-nxg-retail-vue-sdk'

type IntegratorChildDefinition = IntegratorParentPOSConfig['children'][number]
type CmsConfigLookup = (gameId: string) => CmsPosGameConfiguration | null | undefined

type ResolveIntegratorIdsOptions = {
  gameLink: GameLinkView
  cmsConfig?: CmsPosGameConfiguration | null
}

type ResolveProductLinkCodeOptions = {
  childId: string
  sourceProduct?: IntegratorProductTarget
  gameLinks: GameLinkView[]
  getCmsConfig: CmsConfigLookup
}

const resolveIntegratorIds = ({
  gameLink,
  cmsConfig,
}: ResolveIntegratorIdsOptions): { childId: string; frameId: string } => {
  const { childId: configuredChildId, frameId: configuredFrameId } =
    createPosGameConfiguration(cmsConfig)
  const childId = configuredChildId ?? gameLink.code
  const frameId = configuredFrameId ?? gameLink.code

  return { childId, frameId }
}

const toActiveProduct = (gameLink: GameLinkView): IntegratorActiveProduct => {
  const gameShortcuts = getGameShortcuts(gameLink)

  return {
    gameId: gameLink.installation.game.externalId.toString(),
    linkCode: gameLink.code,
    ...(gameShortcuts.length ? { shortcuts: gameShortcuts } : {}),
  }
}

const resolveCmsConfig = (gameId: string | undefined, getCmsConfig: CmsConfigLookup) =>
  gameId ? getCmsConfig(gameId) : undefined

const resolveLinkCodeFromChildId = (
  childId: string,
  gameLinks: GameLinkView[],
  getCmsConfig: CmsConfigLookup,
): string | undefined => {
  const match = gameLinks.find((link) => {
    const { childId: configuredChildId } = createPosGameConfiguration(
      resolveCmsConfig(link.installation.game.id, getCmsConfig),
    )
    const resolvedChildId = configuredChildId ?? link.code

    return resolvedChildId === childId || link.code === childId
  })

  return match?.code
}

const resolveProductLinkCode = ({
  childId,
  sourceProduct,
  gameLinks,
  getCmsConfig,
}: ResolveProductLinkCodeOptions): string | undefined => {
  if (sourceProduct?.linkCode) {
    return sourceProduct.linkCode
  }

  return resolveLinkCodeFromChildId(childId, gameLinks, getCmsConfig)
}

const getGameShortcuts = (gameLink: GameLinkView) => {
  const { getGameShortcutsByDisplayId } = useShortcutsStore()
  const { displayId } = gameLink.installation.game
  const gameShortcuts = displayId ? getGameShortcutsByDisplayId(displayId) : []

  return gameShortcuts.map((gameShortcut) => ({
    actionCode: gameShortcut.actionCode,
    shortcut: gameShortcut.shortcut,
  }))
}

const buildIntegratorChildren = (
  gameLinks: GameLinkView[],
  getCmsConfig: CmsConfigLookup,
): IntegratorChildDefinition[] => {
  const groups = new Map<string, GameLinkView[]>()

  for (const gameLink of gameLinks) {
    const cmsConfig = resolveCmsConfig(gameLink.installation.game.id, getCmsConfig)
    const { childId } = resolveIntegratorIds({ gameLink, cmsConfig })
    const group = groups.get(childId) ?? []

    group.push(gameLink)
    groups.set(childId, group)
  }

  return [...groups.entries()].map(([childId, links]) => {
    const firstLink = links[0]
    const cmsConfig = resolveCmsConfig(firstLink.installation.game.id, getCmsConfig)
    const { frameId } = resolveIntegratorIds({ gameLink: firstLink, cmsConfig })
    const gameOptions: IntegratorInitGameOptions = {}

    const gameShortcuts = links.length === 1 ? getGameShortcuts(firstLink) : []
    if (gameShortcuts.length) {
      gameOptions['shortcuts'] = gameShortcuts
    }

    if (links.length > 1) {
      gameOptions['activeProducts'] = links.map(toActiveProduct)
    }

    const child: IntegratorChildDefinition = {
      frameId,
      childId,
    }

    if (Object.keys(gameOptions).length) {
      child.gameOptions = gameOptions
    }

    return child
  })
}

export {
  buildIntegratorChildren,
  resolveIntegratorIds,
  resolveLinkCodeFromChildId,
  resolveProductLinkCode,
  toActiveProduct,
}
export type { CmsConfigLookup, IntegratorChildDefinition }
